import React, { useState } from 'react';
import { PatientInfoFormData } from './PatientInfoStep';
import { bookAppointment, rescheduleAppointment } from '../../utils/apis';
import styles from './ConfirmationStep.module.css';
import { AxisFailurePopup } from './AxisFailurePopup';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import AxisLogo from './SVGs/AxisLogo';

interface ConfirmationStepProps {
  onBack: () => void;
  onSuccess: () => void;
  provider: Provider;
  slot: any;
  appointmentType: 'telehealth' | 'inperson' | 'spravato' | null;
  patientInfo: PatientInfoFormData | null;
  patientStatus?: 'existing' | 'new';
  patientData?: any;
  reschedulingAppointment?: any;
}

interface Provider {
  providerId: string;
  displayName: string;
  schedulingName: string;
  firstName: string;
  lastName: string;
  specialty: string;
  departmentId: string;
  departmentName: string;
  departmentZip: string;
  departmentAddress: string;
  departmentCity: string;
  departmentState: string;
  availableSlots: {
    startTime: string;
    endTime: string;
    duration: number;
    appointmentId: string;
    appointmentName: string;
    appointmentTypeId: string;
  }[];
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ onBack, onSuccess, provider, slot, appointmentType, patientInfo, patientStatus, patientData, reschedulingAppointment }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Extract patient data with proper fallback logic
  const getPatientData = () => {
    // For existing patients, use patientData from PatientStatusStep
    if (patientData?.athenaPatientId) {
      return patientData;
    }
    // For new patients, use patientInfo from PatientInfoStep
    return patientInfo || patientData;
  };

  const currentPatientData = getPatientData();

  const handleBook = async () => {
    if (!provider || !slot || !appointmentType || !patientStatus) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Format date of birth for API (normalize to MM/DD/YYYY)
      const formatDateOfBirth = (dob: any) => {
        const monthNameToNumber: Record<string, string> = {
          january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
          july: '07', august: '08', september: '09', october: '10', november: '11', december: '12'
        };
        if (typeof dob === 'string') {
          const trimmed = dob.trim();
          // If format like DD/Month/YYYY (e.g., 24/August/1970)
          const parts = trimmed.split('/');
          if (parts.length === 3 && isNaN(Number(parts[1]))) {
            const [ddRaw, monthName, yyyy] = parts;
            const dd = ddRaw.padStart(2, '0');
            const mm = (monthNameToNumber[monthName.toLowerCase()] || monthName).toString().padStart(2, '0');
            return `${mm}/${dd}/${yyyy}`;
          }
          // If ISO YYYY-MM-DD
          if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const [yyyy, mm, dd] = trimmed.split('-');
            return `${mm}/${dd}/${yyyy}`;
          }
          // If already looks like MM/DD/YYYY just return
          return trimmed;
        }
        const { day, month, year } = dob || {};
        if (!day || !month || !year) {
          throw new Error('Date of birth is incomplete');
        }
        const mm = (monthNameToNumber[(month as string).toLowerCase()] || `${month}`).padStart(2, '0');
        const dd = `${day}`.padStart(2, '0');
        return `${mm}/${dd}/${year}`;
      };

      // Extract appointment date and time from slot.startTime
      let appointmentDate = '';
      let appointmentTime = '';
      
      if (slot.startTime) {
        if (slot.startTime.includes('T')) {
          // Format: "09/15/2025T08:30"
          const [datePart, timePart] = slot.startTime.split('T');
          appointmentDate = datePart; // "09/15/2025"
          appointmentTime = timePart; // "08:30"
        } else {
          // Fallback: if no 'T' separator, assume it's just time
          appointmentTime = slot.startTime;
          // Use today's date as fallback
          const today = new Date();
          const month = (today.getMonth() + 1).toString().padStart(2, '0');
          const day = today.getDate().toString().padStart(2, '0');
          const year = today.getFullYear();
          appointmentDate = `${month}/${day}/${year}`;
        }
      }

      // Prepare appointment booking data
      const appointmentData: any = {
        appointmentId: slot.appointmentId, // Use the slot ID from open slots API
        departmentId: provider.departmentId,
        providerId: provider.providerId,
        appointmentTypeId: slot.appointmentTypeId,
        reason: appointmentType === 'telehealth' ? 'Telehealth consultation' : 
                appointmentType === 'spravato' ? 'Spravato treatment' : 'In-person consultation',
        reasonType: patientStatus,
        insuranceCompany: patientInfo?.insurance || 'Unknown',
        insuranceIdNumber: patientInfo?.insuranceId || 'N/A',
        // Add appointment date and time
        appointmentDate: appointmentDate,
        appointmentTime: appointmentTime
      };

      // Add patient ID for existing patients
      if (patientData?.athenaPatientId) {
        appointmentData.patientId = patientData.athenaPatientId;
      }

      // Add patient details for new patients
      if (patientStatus === 'new') {
        // Use patientInfo if available (from PatientInfoStep), otherwise use patientData (from PatientStatusStep)
        const patientDetails = currentPatientData;
        appointmentData.firstName = patientDetails.firstName;
        appointmentData.lastName = patientDetails.lastName;
        appointmentData.dateOfBirth = formatDateOfBirth(patientDetails.dateOfBirth);
        // Convert sex to single letter format (M/F/O/U)
        const formatSex = (sex: string) => {
          if (!sex) return 'U';
          const upperSex = sex.toUpperCase();
          if (upperSex === 'MALE' || upperSex === 'M') return 'M';
          if (upperSex === 'FEMALE' || upperSex === 'F') return 'F';
          if (upperSex === 'OTHER' || upperSex === 'O') return 'O';
          return 'U'; // Unknown
        };
        appointmentData.sex = formatSex(patientDetails.sex);
        appointmentData.phone = patientDetails.phoneNumber;
        // Normalize phone type from UI values to API values
        const normalizePhoneType = (phoneType: string) => {
          if (!phoneType) return 'mobile'; // Default to mobile
          const normalized = phoneType.toLowerCase();
          // Map UI values to API values: Mobile/Work -> mobile, Home -> home
          return normalized === 'home' ? 'home' : 'mobile';
        };
        appointmentData.phoneType = normalizePhoneType(patientDetails.phoneType);
        appointmentData.email = patientDetails.email;
        appointmentData.address1 = patientDetails.address || 'Not provided';
        appointmentData.city = 'Aurora'; // Default city
        appointmentData.state = 'CO'; // Default state
        appointmentData.zip = patientDetails.zipCode || 'Not provided';
      }

      // Handle rescheduling vs new booking
      if (reschedulingAppointment) {
        // Reschedule existing appointment
        await rescheduleAppointment({
          appointmentId: reschedulingAppointment.athenaAppointmentId,
          targetAppointmentId: slot.appointmentId,
          departmentId: provider.departmentId,
          providerId: provider.providerId,
          patientId: patientData?.athenaPatientId,
          bookingNote: 'Patient requested reschedule'
        });
      } else {
        // Book new appointment
        await bookAppointment(appointmentData);
      }
      
      setSuccess(true);
      // Redirect to thank you page after successful booking
      window.location.href = 'https://axismh.com/thank-you/';
      onSuccess();
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'booking appointment');
      setError(userFriendlyError);
    } finally {
      setLoading(false);
    }
  };


  const formatDateTime = (dateTime: string) => {
    try {
      // Convert the date format from "09/08/2025T08:00" to ISO format
      const parts = dateTime.split('T');
      if (parts.length === 2) {
        const datePart = parts[0]; // "09/08/2025"
        const timePart = parts[1]; // "08:00"
        const [month, day, year] = datePart.split('/');
        const isoDateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}:00`;
        const date = new Date(isoDateString);
        return date.toLocaleString([], { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true 
        });
      }
      const date = new Date(dateTime);
      return date.toLocaleString([], { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });
    } catch (error) {
      // Fallback to original string if parsing fails
      return dateTime;
    }
  };

  return (
    <div className={styles.confirmation}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <AxisLogo variant="white" size="small" className={styles.navLogo} />
        <span className={styles.navPhone}>(720) 400-7025</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>BOOK ONLINE</button>
        </div>
        {/* <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div> */}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}>
          {patientData?.athenaPatientId ? 'Confirm Your Appointment' : 'Patient Information'}
        </h1>
        <p className={styles.subtitle}>
          {patientData?.athenaPatientId 
            ? 'Please review your appointment details and confirm your booking.'
            : 'Please provide your personal and insurance information to complete your booking.'
          }
        </p>

        <div className={styles.cardsContainer}>
          {/* Appointment Details Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div className={styles.cardTitle}>Appointment Details</div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.providerName}>{provider?.firstName} {provider?.lastName}</div>
              <div className={styles.providerAddress}>{provider?.departmentAddress || provider?.departmentName}, {provider?.departmentCity}, {provider?.departmentState} {provider?.departmentZip}</div>
              <div className={styles.providerPhone}>(720) 400-7025</div>
              
              <div className={styles.divider} />
              
              <div className={styles.tagsContainer}>
                <div className={styles.tagRow}>
                  <div className={styles.tagLabel}>Date & Time:</div>
                  <div className={styles.tag}>
                    {slot?.startTime ? formatDateTime(slot.startTime) : ''}
                  </div>
                </div>
                <div className={styles.tagRow}>
                  <div className={styles.tagLabel}>Type:</div>
                  <div className={styles.tag}>
                    {appointmentType === 'telehealth' ? 'Telehealth' : 'In-person'}
                  </div>
                </div>
              </div>
              
              <div className={styles.divider} />
              
              <div className={styles.reasonLabel}>Reason for Visit</div>
              <div className={styles.reasonText}>
                {patientInfo?.reasonForVisit || 'Not specified'}
              </div>
            </div>
          </div>

          {/* Patient Information Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <div className={styles.cardTitle}>
                {patientData?.athenaPatientId ? 'Your Information' : 'Patient Information'}
              </div>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.patientName}>
                {currentPatientData?.firstName} {currentPatientData?.lastName}
              </div>
              <div className={styles.patientContact}>
                <div>{currentPatientData?.email}</div>
                <div>{currentPatientData?.phoneNumber || currentPatientData?.phone}</div>
              </div>
              
              <div className={styles.divider} />
              
              <div className={styles.patientDetails}>
                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>Date of Birth:</div>
                  <div className={styles.detailValue}>
                    {(() => {
                      const dob = currentPatientData?.dateOfBirth;
                      if (typeof dob === 'string') {
                        // Handle string format from PatientStatusStep (MM/DD/YYYY)
                        return dob;
                      } else if (dob && typeof dob === 'object') {
                        // Handle object format from PatientInfoStep
                        return `${dob.month} ${dob.day}, ${dob.year}`;
                      }
                      return '';
                    })()}
                  </div>
                </div>
                <div className={styles.detailRow}>
                  <div className={styles.detailLabel}>Sex:</div>
                  <div className={styles.detailValue}>
                    {currentPatientData?.sex || 'Not specified'}
                  </div>
                </div>
              </div>
              
              {/* Show address and insurance info for new patients or if available */}
              {patientInfo && (patientInfo?.address || patientInfo?.zipCode || patientInfo?.insurance || patientInfo?.insuranceId) && (
                <>
                  <div className={styles.divider} />
                  
                  <div className={styles.addressLabel}>Address:</div>
                  <div className={styles.addressText}>
                    <div>{patientInfo?.address || 'Not provided'}</div>
                    <div>{patientInfo?.zipCode || ''}</div>
                  </div>
                  
                  <div className={styles.divider} />
                  
                  <div className={styles.patientDetails}>
                    <div className={styles.detailRow}>
                      <div className={styles.detailLabel}>Insurance:</div>
                      <div className={styles.detailValue}>{patientInfo?.insurance || 'Not provided'}</div>
                    </div>
                    <div className={styles.detailRow}>
                      <div className={styles.detailLabel}>Member ID:</div>
                      <div className={styles.detailValue}>{patientInfo?.insuranceId || 'Not provided'}</div>
                    </div>
                  </div>
                </>
              )}
              
              {/* For existing patients without detailed info, show a note */}
              {patientData?.athenaPatientId && !patientInfo && (
                <>
                  <div className={styles.divider} />
                  <div className={styles.existingPatientNote}>
                    ✓ Your information is on file with our system
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          className={styles.bookButton}
          onClick={handleBook}
          disabled={loading || success}
        >
          {loading 
            ? (reschedulingAppointment ? 'Rescheduling...' : 'Booking...') 
            : (reschedulingAppointment ? 'Reschedule Appointment' : 'Book Appointment')
          }
        </button>

        {/* Friendly error modal */}
        <AxisFailurePopup
          isOpen={!!error}
          message={error || ''}
          onClose={() => setError(null)}
          title="We couldn't complete your booking"
        />

        {success && <div className={`${styles.message} ${styles.successMessage}`}>Appointment booked successfully!</div>}

        <button className={styles.backButton} onClick={onBack} disabled={loading}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): 720-400-7025</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export default ConfirmationStep; 